import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { PathLayer, IconLayer } from '@deck.gl/layers'
import { DeckOverlay } from 'components/common/map/webgl/DeckOverlay'
import { useMapViewState } from 'components/route-manager/RouteManagerMapV2/hooks/useMapViewState'
import { hexToRgb } from 'components/common/map/webgl/generators/colorUtils'
import { decodeEncodedPolyline } from 'utils/decodeEncodedPolyline'
import { buildArrowIcon, buildArrowsAlongPath } from 'components/common/map/cachedDirectionsArrows'

// Tighter pixel spacing as zoom climbs so street views feel populated; clamp
// at the top so country-level views don't drown the GPU.
const pickTargetPx = zoom => {
  if (zoom >= 16) return 30
  if (zoom >= 13) return 50
  if (zoom >= 9) return 80
  return 100
}

const intervalForZoom = (zoom, lat) => {
  const EARTH_CIRCUMFERENCE_AT_EQUATOR = 156543.03392
  const mpp = (EARTH_CIRCUMFERENCE_AT_EQUATOR * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom
  return Math.max(20, pickTargetPx(zoom) * mpp)
}

/**
 * Renders the cached Routes-API polyline plus directional chevrons as deck.gl
 * layers. Lives inside the APIProvider so it can read map zoom + viewport and
 * pick a geo interval that stays close to the DOM Polyline's pixel density.
 *
 * Layered in its own DeckOverlay below the marker overlay (`zIndex` prop) so
 * markers stay on top — both overlays are sibling DOM elements in the same
 * map pane, so CSS zIndex resolves their stacking correctly.
 */
const CachedDirectionsDeckLayer = ({ encodedPath, color, zIndex = 5 }) => {
  const { zoom, bounds } = useMapViewState()

  const layers = useMemo(() => {
    if (!encodedPath) return []
    const path = decodeEncodedPolyline(encodedPath)
    if (path.length < 2) return []

    const [r, g, b] = hexToRgb(color)
    const pathLayer = new PathLayer({
      id: 'cached-directions-path',
      data: [{ path }],
      getPath: d => d.path,
      getColor: [r, g, b, 204],
      getWidth: 4,
      widthUnits: 'pixels',
      capRounded: true,
      pickable: false,
    })

    const centerLat = bounds ? (bounds[1] + bounds[3]) / 2 : 0
    const arrows = buildArrowsAlongPath(path, intervalForZoom(zoom, centerLat))
    if (arrows.length === 0) return [pathLayer]

    const arrowIcon = buildArrowIcon(color)
    const arrowLayer = new IconLayer({
      id: 'cached-directions-arrows',
      data: arrows,
      getIcon: () => arrowIcon,
      getPosition: d => d.position,
      getAngle: d => d.angle,
      getSize: 18,
      sizeUnits: 'pixels',
      pickable: false,
    })

    return [pathLayer, arrowLayer]
  }, [encodedPath, color, zoom, bounds])

  if (layers.length === 0) return null
  return <DeckOverlay layers={layers} style={{ zIndex }} />
}

CachedDirectionsDeckLayer.propTypes = {
  encodedPath: PropTypes.string,
  color: PropTypes.string.isRequired,
  zIndex: PropTypes.number,
}

export default CachedDirectionsDeckLayer
